import { iconChevronRight, iconFolder, iconLock } from "@kensho/icons";
import { ButtonLink } from "@kensho/neo";
import { use } from "react";

import batchImg from "../assets/batch.svg";
import transcriptionInterfaceImg from "../assets/transcription-interface.png";
import wordmark from "../assets/wordmark.svg";
import { BasePage } from "../components/base-page";
import { GraphicElement } from "../components/graphic-element";
import { Section } from "../components/section";
import { URLS } from "../constants";
import { SiteAnalyticsContext } from "../providers/site-analytics-context";
import { UserContext } from "../providers/user-context";

export function HomePage() {
  const { user, _state: userState } = use(UserContext);
  const analytics = use(SiteAnalyticsContext);

  return (
    <BasePage>
      <article>
        <header className="ml-2 max-w-[1000px] lg:mx-auto">
          <img
            className="mx-0 mt-12 mb-0 inline w-[350px]"
            src={wordmark}
            alt="Kensho Scribe"
          />
          <h1 className="mb-0 text-3xl font-bold lg:mt-7">
            Transcribe Business Audio with unparalleled accuracy, speed, and
            security.
          </h1>
        </header>
        <Section>
          <div className="order-2 flex-auto basis-1/2 md:order-none">
            <h2 className="m-0 mb-3 text-xl font-bold md:mb-6">
              Transcribe your files without a single line of code with the
              Scribe Interface.
            </h2>
            <p className="mb-6 text-base lg:mb-14">
              Don’t want to write code? No problem! Drag files into the Scribe
              Interface and get a downloadable transcript with ease.
            </p>
            <ButtonLink
              href="/transcriptions"
              intent="primary"
              rightIcon={iconChevronRight}
              size="large"
            >
              Use Scribe Interface
            </ButtonLink>
          </div>
          <div className="mx-2 flex-auto basis-1/2 md:mx-4">
            <img
              src={transcriptionInterfaceImg}
              alt="LandingPage"
              className="md:order-non order-1 mb-7 w-full shadow-sm md:mb-0"
            />
          </div>
        </Section>

        <Section
          backgroundColor="#F5FAFC"
          title="API Guides & Tutorials"
          description="Step-by-step guides for building tools with the Scribe Batch and Websocket APIs"
        >
          <div className="mx-auto grid grid-cols-1 gap-8 sm:grid-cols-2 sm:py-10 lg:grid-cols-2">
            <GraphicElement
              heading="Authentication Quickstart"
              description="Get started generating API tokens to interact with Scribe"
              label="Get started"
              href="https://docs.kensho.com/authentication/personal-token"
              icon={iconLock}
            />
            <GraphicElement
              heading="Batch API Quickstart"
              description="Transcribe audio and video files using Scribe’s Batch API"
              label="Get started"
              href="https://docs.kensho.com/scribe/v2/batch-development"
              icon={iconFolder}
            />
          </div>
        </Section>

        <Section
          title="API Reference"
          description="Documentation of each call to and response from the Scribe APIs"
        >
          <div className="flex flex-col gap-4 md:flex-row md:gap-10">
            <div className="flex-1">
              <img src={batchImg} alt="batch file" />
              <h3 className="mt-4 mb-2 md:mt-8">Batch API for Media Files</h3>
              <p className="mb-4 text-base">
                Easily transcribe small or large quantities of business audio
                and video files using the Scribe Batch API for Media Files.
              </p>
              <ButtonLink
                href="https://docs.kensho.com/scribe/v2/batch-api-specification"
                rightIcon={iconChevronRight}
                minimal
                intent="primary"
              >
                Explore the Docs
              </ButtonLink>
            </div>
          </div>
        </Section>
        {userState === "known" && !user?.email && (
          <Section backgroundColor="#F5FAFC">
            <div className="mt-0">
              <h2 className="mb-3 text-xl font-bold">Try Scribe for Free!</h2>
              <p className="mb-6">
                Test Scribe’s transcription on your files with 10 hours for free
              </p>

              <ButtonLink
                href={URLS.FREE_TRIAL}
                intent="primary"
                size="large"
                onClick={() => analytics.sendEvent("request_free_trial")}
              >
                Start a free trial
              </ButtonLink>
            </div>
          </Section>
        )}
      </article>
    </BasePage>
  );
}
